import clsx from "clsx";
import { useEffect, useState } from "react";
import { basisLabel } from "../../shared/money";
import { plural } from "../../shared/words";
import { useDraftInvalid } from "../../web/components/fields";
import { DraftBar } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { groupTaxBasis, itemName, itemTax, membersOf, pricedMembersOf, usePriceListSaver, useStillShown } from "./data";
import { type GroupRow, type GroupTax, type ItemPricing, type PriceListData, type PricingRule } from "./meta";
import { needsCheck, sameRuleShown } from "./pricing";
import { quoteKey } from "./QuotedAmount";
import { basisOfTax } from "./PricingTab";

/*
 * A group's pricing draft (its window, GroupWindow.tsx): whether it's changed, the prices it moves, and its save bar.
 */

/** A group's pricing being changed (in its window), not saved yet: its rule and how it taxes its items. */
export interface GroupDraft {
    rule: PricingRule;
    tax: GroupTax;
}

/** Whether a draft is just the group's pricing as saved (whatever order its fields were set in). */
export function sameDraft(draft: GroupDraft, group: GroupRow, rates: PriceListData["taxRates"]) {
    if (quoteKey(draft.tax, rates) !== quoteKey(group.tax, rates)) return false;
    return sameRuleShown(draft.rule, group.rule, basisOfTax(draft.tax, rates));
}

/** Each item using the group that the draft would change: its price now, and the price (and tax, if that changes) it would give. */
function groupPreview(data: PriceListData, groupId: string, draft: GroupDraft) {
    const { rate: _rate, ...groupTax } = groupTaxBasis(data, draft.tax);
    const pricing: ItemPricing = { ...draft.rule, appliedPrice: null, groupId, groupTax };
    // Not those overriding its price: the group's pricing doesn't reach them.
    return pricedMembersOf(data, groupId).flatMap((m) => {
        const itemRate = itemTax(data, m);
        const check = needsCheck(m, itemRate, pricing);
        if (!check) return [];
        // The tax it has now and would have: shown when they differ.
        const taxNow = basisLabel(m.price_includes_taxes, itemRate.rate);
        const taxNext = check.taxTo ? basisLabel(check.taxTo.includesTax, groupTaxBasis(data, check.taxTo).rate) : taxNow;
        return [{ m, now: m.price, next: check.target, taxNow, taxNext }];
    });
}

/**
 * Saving a group's pricing draft, like a product's: the prices it moves, then its save bar: Save (its products follow it at once:
 * productsFollow.ts; the items linked to them then show theirs to update), or Cancel.
 */
export function GroupSave(
    props: Readonly<{ data: PriceListData; group: GroupRow; draft: GroupDraft; /** Saving (its fields wait). */ onRunning?: (running: boolean) => void; onDone: () => void }>,
) {
    const { data, group, draft } = props;
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const edited = !sameDraft(draft, group, data.taxRates);
    const moving = edited ? groupPreview(data, group.id, draft) : [];
    // Something typed above can't be taken (the draft has what it had before): not saved until it's put right.
    const typedBad = useDraftInvalid();
    const stillShown = useStillShown();
    const { onRunning } = props;
    useEffect(() => {
        onRunning?.(busy);
        return () => onRunning?.(false);
    }, [busy, onRunning]);

    const save = async () => {
        setError(null);
        setBusy(true);
        try {
            // From a markup: it becomes a fixed price (no tiers).
            await saver.updateGroup(group.id, { basePrice: draft.rule.basePrice, tax: draft.tax, ...(group.tiers ? { tiers: null } : {}) });
            // Another group since (its window moved on): its draft isn't this one's to clear.
            if (stillShown.current) props.onDone();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };

    return (
        // As a product's: each price it moves, then its save bar along the bottom of the editor (edge to edge): what saving does on
        // the left, its buttons on the right (under it only when narrow).
        <>
            {edited ? <PriceChanges data={data} rows={moving} /> : null}
            <div className="-mx-4 -mb-4 border-t border-brand-500/30 px-3 py-2">
                <DraftBar
                    message={
                        edited ? (
                            <>
                                <PriceSummary prices={moving.length} overriding={membersOf(data, group.id).length - pricedMembersOf(data, group.id).length} />
                                {typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                            </>
                        ) : (
                            "Change the pricing above to see what happens to prices."
                        )
                    }
                    busy={busy}
                    error={error}
                    actions={
                        <>
                            {edited ? (
                                <button type="button" className="btn-primary" disabled={busy || typedBad} onClick={() => void save()}>
                                    Save pricing
                                </button>
                            ) : null}
                            <button type="button" className="btn-secondary" disabled={busy} onClick={props.onDone}>
                                Cancel
                            </button>
                        </>
                    }
                />
            </div>
        </>
    );
}

/** What saving a group change will cause, beside its Save: how many prices change (items linked to them then show theirs to update), and its products overriding it keep theirs. */
function PriceSummary({ prices, overriding }: Readonly<{ prices: number; overriding: number }>) {
    // Short (beside its buttons).
    const changes = prices ? `${plural(prices, "price")} ${prices === 1 ? "changes" : "change"}` : "No prices change";
    const after = [prices ? "Items linked to them then show theirs to update." : "", overriding ? (overriding === 1 ? "1 overriding product keeps its own." : `${overriding} overriding products keep theirs.`) : ""].filter(Boolean);
    return (
        <span>
            <span className="font-medium">{changes}</span>
            {after.map((line) => (
                <span key={line} className="block text-xs text-muted">
                    {line}
                </span>
            ))}
        </span>
    );
}

/** Each item a group's pricing changes: its price (now -> new, with its tax when that changes too). */
function PriceChanges(props: Readonly<{ data: PriceListData; rows: ReturnType<typeof groupPreview> }>) {
    const { data, rows } = props;
    const fmt = useMoney();
    if (!rows.length) return null;
    const change = (now: string, next: string, taxNow?: string, taxNext?: string) => (
        <>
            <span className="text-muted">
                {fmt.money(now)}
                {taxNext === taxNow ? null : <span className="text-xs"> {taxNow}</span>}
            </span>{" "}
            → <span className="font-semibold">{fmt.money(next)}</span>
            {taxNext === taxNow ? null : <span className="text-xs text-muted"> {taxNext}</span>}
        </>
    );
    // A row: on the list's columns (subgrid), so they line up whatever's in them.
    const row = "col-span-2 grid grid-cols-subgrid items-center gap-x-4 px-3 py-1.5";
    return (
        <div>
            {/* One grid for the list: the item and its price; headed by what each column is. */}
            <ul className="grid grid-cols-[minmax(0,1fr)_auto] divide-y divide-line rounded-md border border-line bg-surface">
                <li className={clsx(row, "text-xs font-medium text-muted")}>
                    <span>Item</span>
                    <span className="text-right">Price</span>
                </li>
                {rows.map((p) => (
                    <li key={p.m.uuid} className={row}>
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate">{itemName(data, p.m)}</span>
                        </span>
                        <span className="text-right whitespace-nowrap tabular-nums">{change(p.now, p.next, p.taxNow, p.taxNext)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
